import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Receipt, ArrowLeft, ExternalLink, AlertCircle, FileText } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { SubscriptionCard } from '@/components/payment/SubscriptionCard';
import { stripeService } from '@/services/stripe-service';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/utils/supabase';

export const BillingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState<any | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [portalLoading, setPortalLoading] = useState(false);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [hasStripeCustomer, setHasStripeCustomer] = useState(false);

  useEffect(() => {
    const loadBillingData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Récupérer les informations d'abonnement directement depuis la table profiles
        const { data: profileData, error: profileError } = await supabase
          .from('profiles')
          .select('subscription_id, subscription_status')
          .eq('id', user?.id)
          .single();
        
        if (profileError) {
          console.error('Error fetching profile:', profileError);
          setError('Erreur lors du chargement des informations d\'abonnement');
        } else if (profileData && profileData.subscription_id) {
          // Créer un objet de subscription à partir des données du profil
          const subscriptionData = {
            subscription_id: profileData.subscription_id,
            subscription_status: profileData.subscription_status,
            current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).getTime(), // +30 jours par défaut
            cancel_at_period_end: false
          };
          setSubscription(subscriptionData);
        }
        
        const { data: customerData } = await supabase
          .from('stripe_customers')
          .select('customer_id')
          .eq('user_id', user?.id)
          .is('deleted_at', null)
          .maybeSingle();

        setHasStripeCustomer(!!customerData?.customer_id);

      } catch (error) {
        console.error('Load billing data error:', error);
        setError(error.message || 'Erreur lors du chargement des données de facturation');
      } finally {
        setLoading(false);
      }
    };
    
    loadBillingData();
  }, [user?.id]);

  const handleManageSubscription = async () => {
    try {
      setPortalLoading(true);

      // Utiliser le service Stripe pour accéder au portail client
      const portalUrl = await stripeService.accessCustomerPortal();

      window.open(portalUrl, '_blank');
      
    } catch (error) {
      console.error('Error accessing customer portal:', error);
      setError(error.message || 'Erreur lors de l\'accès au portail client Stripe');
    } finally {
      setPortalLoading(false);
    }
  };

  const handleViewInvoices = async () => {
    try {
      setInvoicesLoading(true);
      const portalUrl = await stripeService.accessInvoicesPortal();
      window.open(portalUrl, '_blank');
    } catch (error) {
      console.error('Error accessing invoices portal:', error);
      setError(error.message || 'Erreur lors de l\'accès aux factures');
    } finally {
      setInvoicesLoading(false);
    }
  };

  const handleUpgradeSubscription = () => {
    navigate('/pricing');
  };

  return (
    <div className="max-w-4xl mx-auto">
      <Helmet>
        <title>Facturation et abonnement - DPEWizard</title>
        <meta name="description" content="Gérez votre abonnement DPEWizard, consultez votre historique de facturation et vos moyens de paiement." />
      </Helmet>
      
      <div className="mb-8">
        <Button
          variant="ghost"
          size="sm"
          className="mb-4"
          onClick={() => navigate('/dashboard')}
          icon={<ArrowLeft className="w-4 h-4 mr-2" />}
        >
          Retour au tableau de bord
        </Button>
        
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Facturation et abonnement
        </h1>
        <p className="text-gray-600">
          Gérez votre abonnement, vos moyens de paiement et consultez votre historique de facturation
        </p>
      </div>

      {error && (
        <Card className="mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-3 text-red-600">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>{error}</p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Subscription */}
        <div className="space-y-6">
          {loading ? (
            <Card>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-6 bg-gray-200 rounded w-1/3"></div>
                  <div className="h-10 bg-gray-200 rounded w-1/2"></div>
                  <div className="h-4 bg-gray-200 rounded w-full"></div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <SubscriptionCard
              subscription={subscription}
              onManage={handleManageSubscription}
              onUpgrade={handleUpgradeSubscription}
              loading={portalLoading}
            />
          )}
        </div>
        
        {/* Invoices & Help */}
        <div className="space-y-6">
          {(hasStripeCustomer || subscription) && (
            <Card className="border-blue-100 bg-blue-50/30">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h3 className="font-semibold text-gray-900">Mes factures</h3>
                </div>

                <p className="text-sm text-gray-600 mb-4">
                  Consultez et téléchargez toutes vos factures au format PDF depuis votre espace Stripe.
                </p>

                <Button
                  variant="primary"
                  size="sm"
                  className="w-full"
                  onClick={handleViewInvoices}
                  loading={invoicesLoading}
                  disabled={invoicesLoading}
                  icon={<ExternalLink className="w-4 h-4 mr-2" />}
                >
                  {invoicesLoading ? 'Redirection...' : 'Voir mes factures'}
                </Button>
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <Receipt className="w-5 h-5 text-blue-500" />
                <h3 className="font-semibold text-gray-900">Besoin d'aide ?</h3>
              </div>
              
              <p className="text-sm text-gray-600 mb-4">
                Pour toute question concernant votre facturation ou votre abonnement, n'hésitez pas à contacter notre support.
              </p>
              
              <Button
                variant="outline"
                size="sm"
                className="w-full"
                onClick={() => window.open('mailto:support@dpewizard.com')}
              >
                Contacter le support
              </Button>
            </CardContent>
          </Card>
          
        </div>
      </div>
    </div>
  );
};

export default BillingPage;